import React, {
  useState,
  useCallback,
  useContext,
  ReactNode,
  ReactElement,
} from "react";
import { showErrorMessage } from "usecases/commons/show-error-message";
import RecurrentPaymentActionsContext from "./context";
import CompanyContext from "../../data/CompanyWrapper/context";
import ModalContext from "../../ModalWrapper/context";
import RecurrentPaymentContext from "../../data/RecurrentPaymentsWrapper/context";
import approveHHRREducationRequest from "../../../../../usecases/recurrent-payments/approve-hhrr-education";
import approveFinanceEducationRequest from "../../../../../usecases/recurrent-payments/approve-finance-education";
import rejectEducationRequest from "../../../../../usecases/recurrent-payments/reject-education";
import rejectRentingRequest from "../../../../../usecases/recurrent-payments/reject-renting";
import reviewRentingDetailsRequest from "../../../../../usecases/recurrent-payments/review-renting-details";
import RentingResultsModal from "../../../../../app/components/modals/recurrentPayments/RentingResultsModal";
import approveRentingRequest from "../../../../../usecases/recurrent-payments/approve-renting";
import downLoadRentingStepFile from "../../../../../usecases/recurrent-payments/dowload-renting-file";
import sendRecurrentPaymentReportEmail from "../../../../../usecases/recurrent-payments/send-report-email";
import { translate as t } from "../../../../translate/localization";

interface Props {
  children: ReactNode;
}

export default function RecurrentPaymentActionWrapper(
  props: Props
): ReactElement {
  const { company } = useContext(CompanyContext);
  const { id: companyId } = company;
  const {
    loadRecurrentPaymentDetail,
    rentingInitialData,
    setRentingDetails,
    setRentingInitialData,
  } = useContext(RecurrentPaymentContext);
  const { openModal, closeModal } = useContext(ModalContext);

  const [approveHHRREducationIsLoading, setApproveHHRREducationIsLoading] =
    useState<boolean>(false);

  const approveHHRREducation = useCallback(
    async ({
      requestId,
      employeeAmount,
      companyAmount,
    }: {
      requestId: string;
      employeeAmount: number;
      companyAmount: number;
    }): Promise<void> => {
      setApproveHHRREducationIsLoading(true);
      await approveHHRREducationRequest({
        companyId,
        requestId,
        employeeAmount,
        companyAmount,
      });
      setApproveHHRREducationIsLoading(false);
      closeModal();
      await loadRecurrentPaymentDetail({
        companyId,
        recurrentPaymentId: requestId,
      });
    },
    [closeModal, companyId, loadRecurrentPaymentDetail]
  );

  const [
    approveFinanceEducationIsLoading,
    setApproveFinanceEducationIsLoading,
  ] = useState<boolean>(false);

  const approveFinanceEducation = useCallback(
    async ({
      requestId,
      employeeAmount,
      companyAmount,
      splitBy,
    }): Promise<void> => {
      setApproveFinanceEducationIsLoading(true);
      await approveFinanceEducationRequest({
        companyId,
        requestId,
        employeeAmount,
        companyAmount,
        splitBy,
      });
      setApproveFinanceEducationIsLoading(false);
      closeModal();
      await loadRecurrentPaymentDetail({
        companyId,
        recurrentPaymentId: requestId,
      });
    },
    [closeModal, companyId, loadRecurrentPaymentDetail]
  );

  const [reviewRentingDetailsIsLoading, setReviewRentingIsLoading] =
    useState<boolean>(false);

  const reviewRentingDetails = useCallback(
    async ({ requestId, rentingData }): Promise<void> => {
      setReviewRentingIsLoading(true);
      setRentingInitialData(rentingData);
      try {
        await reviewRentingDetailsRequest({
          companyId,
          requestId,
          rentingData,
          callback: (data) => {
            closeModal();
            setRentingDetails(data);
          },
        });
        openModal({
          title: t("recurrent-payments-section.renting-results"),
          modalContents: <RentingResultsModal />,
        });
      } catch (error) {
        showErrorMessage(t("common-messages.basic-error-message"));
      } finally {
        setReviewRentingIsLoading(false);
      }
    },
    [closeModal, companyId, setRentingDetails, openModal, setRentingInitialData]
  );

  const [approveRentingIsLoading, setApproveRentingIsLoading] = useState(false);

  const approveRenting = useCallback(
    async ({ requestId }): Promise<void> => {
      setApproveRentingIsLoading(true);
      await approveRentingRequest({
        companyId,
        requestId,
        rentingData: rentingInitialData,
      });
      setApproveRentingIsLoading(false);
      closeModal();
      await loadRecurrentPaymentDetail({
        companyId,
        recurrentPaymentId: requestId,
      });
    },
    [closeModal, companyId, loadRecurrentPaymentDetail, rentingInitialData]
  );

  const [rejectEducationIsLoading, setRejectEducationIsLoading] =
    useState<boolean>(false);

  const rejectEducation = useCallback(
    async ({ requestId, rejectedReason, category }): Promise<void> => {
      setRejectEducationIsLoading(true);
      await rejectEducationRequest({
        companyId,
        requestId,
        rejectedReason,
        category,
      });
      setRejectEducationIsLoading(false);
      closeModal();
      await loadRecurrentPaymentDetail({
        companyId,
        recurrentPaymentId: requestId,
      });
    },
    [closeModal, companyId, loadRecurrentPaymentDetail]
  );

  const [rejectRentingIsLoading, setRejectRentingIsLoading] =
    useState<boolean>(false);

  const rejectRenting = useCallback(
    async ({ requestId, rejectedReason, category }): Promise<void> => {
      setRejectRentingIsLoading(true);
      await rejectRentingRequest({
        companyId,
        requestId,
        rejectedReason,
        category,
      });
      setRejectRentingIsLoading(false);
      closeModal();
      await loadRecurrentPaymentDetail({
        companyId,
        recurrentPaymentId: requestId,
      });
    },
    [closeModal, companyId, loadRecurrentPaymentDetail]
  );

  const downloadRentingFile = useCallback(
    async ({ fileId, fileName }): Promise<void> => {
      await downLoadRentingStepFile({
        fileId,
        fileName,
      });
    },
    []
  );

  const sendReportEmail = useCallback(
    async ({ category, email, ...params }): Promise<void> => {
      await sendRecurrentPaymentReportEmail({
        companyId,
        category,
        email,
        filters: params,
      });
      closeModal();
    },
    [closeModal, companyId]
  );

  return (
    <RecurrentPaymentActionsContext.Provider
      value={{
        approveHHRREducation,
        approveHHRREducationIsLoading,
        approveFinanceEducation,
        approveFinanceEducationIsLoading,
        rejectEducation,
        rejectEducationIsLoading,
        rejectRenting,
        rejectRentingIsLoading,
        reviewRentingDetails,
        reviewRentingDetailsIsLoading,
        approveRenting,
        approveRentingIsLoading,
        downloadRentingFile,
        sendReportEmail,
      }}
      {...props}
    />
  );
}
